import PatientDetailsList from './patient-details-list.component'
import EditPatientModalComponent from './edit-patient-modal.component'
import { TPatient } from 'src/types/patient'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { twMerge } from 'src/utils/tw-merge.util'
import { useState } from 'react'
import { CurecallButton, ECurecallButtonAppearance, ECurecallSize, PenIcon } from '@honestica/curecall-ui'
import { getPatientCivilityAndFullName } from 'src/utils/string-formatter.util'
import PatientGenderIcon from 'src/components/patient/patient-gender-icon.component'
import { WithPermission, WithSpecialAuthorization } from 'src/providers/current-project.provider'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  EPermissionSpecialAuthorization,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { useSession } from 'src/providers/auth.provider'
import { useQueryClient } from '@tanstack/react-query'
import { OverrideChannelModal } from 'src/components/communication-channel/override-channel-modal.component'
import { isCensoredBirthdate } from 'src/utils/date-formatter.util'

type Props = {
  patient: TPatient
}

const PatientInfo = ({ patient }: Props) => {
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false)
  const [isOverrideChannelModalOpen, setIsOverrideChannelModalOpen] = useState(false)
  const { t } = useTranslation()
  const { projectId } = useParams()
  const { currentUser } = useSession()
  const queryClient = useQueryClient()

  const calculateAge = (birthday?: Date | string): number | string => {
    if (!birthday) return ''
    if (isCensoredBirthdate(birthday)) return '**'
    const today = new Date()
    const birthDate = new Date(birthday)
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1
    }
    return age
  }

  const patientTestAccountIcon = patient.isTestAccount ? (
    <span
      className={twMerge(
        'c-rounded-full c-text-white c-w-8 c-h-8 c-leading-10 c-flex c-justify-center  c-font-bold c-mr-2 c-bg-primary-201',
      )}
    >
      T
    </span>
  ) : null

  const patientAge =
    (patient?.birthdate &&
      `( ${calculateAge(patient?.birthdate)} ${t(
        'components.patient.patient-file.header.patient-infos.years',
      )} )`) ||
    ''
  const patientTitle = `${getPatientCivilityAndFullName(t, patient)} ${patientAge}`

  return (
    <div>
      <EditPatientModalComponent
        isOpen={editPatientModalOpen}
        patientId={patient.id}
        onToggle={() => setEditPatientModalOpen(false)}
        projectId={Number(projectId)}
        patient={patient}
      />

      {isOverrideChannelModalOpen && (
        <OverrideChannelModal
          subjectType="PATIENT"
          subjectId={patient.id}
          onClose={() => setIsOverrideChannelModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ['patients', Number(projectId), patient.id],
            })
          }}
        />
      )}

      <div className="c-flex c-flex-col c-gap-2">
        <div className="c-flex c-items-end c-gap-2 c-m-0">
          <h1 className="c-text-lg c-font-bold c-flex c-items-center">
            {patientTestAccountIcon}
            <PatientGenderIcon patient={patient} className="c-mr-2 c-h-8" />
            {patientTitle}
          </h1>
          <WithPermission
            requiredSubject={EPermissionGrantSubjects.PATIENT}
            requiredAction={EPermissionGrantActions.UPDATE}
          >
            <WithSpecialAuthorization
              requiredSpecialAuthorization={EPermissionSpecialAuthorization.CAN_VIEW_PATIENT_IDENTITIES}
            >
              <CurecallButton
                appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
                onClick={() => setEditPatientModalOpen(true)}
                hasIconAtStart
                iconType={<PenIcon />}
                size={ECurecallSize.SMALL}
              >
                {t('common.actions.update')}
              </CurecallButton>
            </WithSpecialAuthorization>
          </WithPermission>
        </div>
        <PatientDetailsList
          patient={patient}
          onOverrideChannel={
            currentUser?.isAdmin ? () => setIsOverrideChannelModalOpen(true) : undefined
          }
        />
      </div>
    </div>
  )
}

export default PatientInfo
